import React, { useCallback, useState, useEffect } from 'react'
import dayjs from 'dayjs'
import { message as antMessage } from 'antd'
import { Picker, Button as MobileButton } from 'antd-mobile'
import AsyncSearchSelect, { SearchOption } from '@/components/common/AsyncSearchSelectResponsive'
import TripSegmentList from './TripSegmentList'
import DateTimePicker from '@/components/common/DateTimePicker'
import { CrossCityTripFormData, FormUpdateParams, CityInfo } from '@/types/forms'
import { apiClient } from '@/services/api'
import type { CityDTO } from '@/types'
import { useIsMobile } from '@/hooks/useIsMobile'
import { validateDestinationCities } from '@/utils/validation'

interface CrossCityTripFormProps {
  formData: CrossCityTripFormData
  onChange: (updates: FormUpdateParams) => void
  disabled?: boolean  // 🔑 新增：是否禁用（查看模式）
}

/**
 * 跨城表单组件
 * 支持单行程和多段行程两种模式的动态切换
 */
const lingxiColumns = [[
  { label: '是', value: 'yes' },
  { label: '否', value: 'no' },
]]

const CrossCityTripForm: React.FC<CrossCityTripFormProps> = ({
  formData,
  onChange,
  disabled = false,  // 🔑 默认不禁用
}) => {
  const isMobile = useIsMobile()
  const [lingxiPickerVisible, setLingxiPickerVisible] = useState(false)

  // 本地字符串 state，解耦显示值与实际数值，允许显示空和 0
  const [expenseInputValue, setExpenseInputValue] = useState<string>(
    formData.expenseType === 'SELF_PAID' ? '0' :
    (formData.estimatedExpense != null ? String(formData.estimatedExpense) : '')
  )

  // 切换费用类型时重置输入框；草稿加载由初始 useState 处理
  useEffect(() => {
    if (formData.expenseType === 'SELF_PAID') {
      setExpenseInputValue('0')
    } else {
      setExpenseInputValue(
        formData.estimatedExpense != null ? String(formData.estimatedExpense) : ''
      )
    }
  }, [formData.expenseType, formData.estimatedExpense])

  // 出发日期变化时，若返回日期早于新出发日期则自动清空返回日期
  const handleStartDateChange = (value: string) => {
    const updates: FormUpdateParams = { startDate: value }
    if (value && formData.endDate && dayjs(formData.endDate).isBefore(dayjs(value))) {
      updates.endDate = ''
    }
    onChange(updates)
  }

  // 处理预估费用失焦时格式化（保留合法数值，清空则不回填 0）
  const handleEstimatedExpenseBlur = () => {
    if (expenseInputValue === '') return
    const numValue = Math.min(9999999999, Math.max(0, parseFloat(expenseInputValue) || 0))
    setExpenseInputValue(String(numValue))
    onChange({ estimatedExpense: numValue })
  }

  // 城市搜索处理函数
  const handleCitySearch = useCallback(
    async (keyword: string) => {
      return await apiClient.searchCities(keyword)
    },
    []
  )

  // 处理出发城市选择
  const handleDepartureChange = (
    _value: string,
    label: string,
    option?: SearchOption
  ) => {
    if (!label || label.trim() === '') {
      onChange({ departureCity: '', departureCityCode: '' })
      return
    }
    const city = option as CityDTO
    onChange({ departureCity: city?.name || label, departureCityCode: city?.code || '' })
  }

  // 处理目的城市添加（多城市 Tag 模式）
  const handleDestinationAdd = (
    _value: string,
    label: string,
    option?: SearchOption
  ) => {
    if (!label || label.trim() === '') return
    const city = option as CityDTO
    const newCity: CityInfo = { name: city?.name || label, code: city?.code || '' }
    const current = formData.destinationCities || []
    // 校验：不重复、不与出发城市相同、不超过 10 个
    const validation = validateDestinationCities([...current, newCity], formData.departureCity)
    if (!validation.isValid) {
      antMessage.warning(validation.message)
      return
    }
    onChange({ destinationCities: [...current, newCity] })
  }

  // 删除目的城市 Tag
  const handleDestinationRemove = (index: number) => {
    const current = formData.destinationCities || []
    if (current.length <= 1) return  // 至少保留 1 个
    onChange({ destinationCities: current.filter((_, i) => i !== index) })
  }

  // 城市选项的自定义渲染格式
  const formatCityOption = (city: SearchOption) => (
    <div className="text-sm font-semibold text-gray-900">
      {city.name}
    </div>
  )

  return (
    <div className="space-y-clay-lg">
      {/* 提示框 */}
      <div className="info-box">
        <p className="body-text font-semibold mb-2">
          说明
        </p>
        <ol className="hint-text text-slate-600 space-y-2 list-decimal list-inside">
          <li>员工因公务离开当前工作城市前往其他城市出差，出差期间的工作日计入考勤。</li>
          <li>出差申请统一通过"百川"系统提交即可，无需在报销系统中重复发起，不影响后续正常报销流程。</li>
          {/* <li>如有同行人员，由其中一人统一发起出差申请，同行人无需重复提交，同行人出差期间的工作日一并记录考勤。</li> */}
          <li>出差申请发起人在报销差旅费时，须将同行人的费用一并报销完毕。</li>
          <li>如同行人与出差申请单发起人隶属于不同部门，建议分别提交出差申请。</li>
        </ol>
      </div>

      {/* 是否在灵犀显示出差状态 */}
      {isMobile ? (
        <div>
          <label className="heading-2 mb-clay-sm block">是否在灵犀显示出差状态 <span className="text-red-500">*</span></label>
          <div
            className={`clay-input w-full flex items-center justify-between ${disabled ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => { if (!disabled) setLingxiPickerVisible(true) }}
          >
            <span>{formData.showInLingxi === false ? '否' : '是'}</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <Picker
            columns={lingxiColumns}
            visible={lingxiPickerVisible}
            onClose={() => {
              setLingxiPickerVisible(false)
              document.body.style.overflow = ''
            }}
            value={[formData.showInLingxi === false ? 'no' : 'yes']}
            onConfirm={v => onChange({ showInLingxi: v[0] === 'yes' })}
          />
        </div>
      ) : (
        <div className="required-field">
          <label className="heading-2 mb-clay-sm block">是否在灵犀显示出差状态 <span className="text-red-500">*</span></label>
          <select
            className="clay-input w-full appearance-none"
            value={formData.showInLingxi === false ? 'no' : 'yes'}
            onChange={(e) => onChange({ showInLingxi: e.target.value === 'yes' })}
            disabled={disabled}
          >
            <option value="yes">是</option>
            <option value="no">否</option>
          </select>
        </div>
      )}

      {/* 单行程模式 - 当 multiSegment = false 时显示 */}
      {!formData.multiSegment && (
        <div className="space-y-clay-md">
          {/* 出发日期和返回日期 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-clay-md">
            {/* 出发日期 */}
            <DateTimePicker
              label="出发日期"
              value={formData.startDate || ''}
              onChange={handleStartDateChange}
              placeholder="请选择出发日期和时间"
              disabled={disabled}
            />

            {/* 返回日期 */}
            <DateTimePicker
              label="返回日期"
              value={formData.endDate || ''}
              onChange={(value) => onChange({ endDate: value })}
              placeholder="请选择返回日期和时间"
              disabled={disabled}
              minDate={formData.startDate || undefined}
              defaultTime={[18, 30]}
            />
          </div>

          {/* 出发城市和目的城市 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* 出发城市 */}
            <div>
              <AsyncSearchSelect
                label="出发城市"
                value={formData.departureCity || ''}
                displayText={formData.departureCity || ''}
                onSearch={handleCitySearch}
                onChange={handleDepartureChange}
                loadDefaultOptions={() => apiClient.getHotCities()}
                formatOption={formatCityOption}
                placeholder="点击选择或搜索..."
                required={true}
                disabled={disabled}
              />
            </div>

            {/* 目的城市（多城市 Tag 选择器） */}
            <div>
              <label className="clay-label text-clay-sm">
                目的城市 <span className="text-red-500">*</span>
              </label>
              {/* 已选城市 Tag 列表 */}
              {(formData.destinationCities || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(formData.destinationCities || []).map((city, idx) => (
                    isMobile ? (
                      <MobileButton
                        key={`dest-${idx}`}
                        shape='rounded'
                        color='primary'
                        size='mini'
                        style={{ '--height': '16px' } as React.CSSProperties}
                        onClick={() => {
                          if (!disabled && (formData.destinationCities || []).length > 1) {
                            handleDestinationRemove(idx)
                          }
                        }}
                      >
                        {city.name}{!disabled && (formData.destinationCities || []).length > 1 ? ' ×' : ''}
                      </MobileButton>
                    ) : (
                      <span
                        key={`dest-${idx}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium"
                      >
                        {city.name}
                        {!disabled && (
                          <button
                            type="button"
                            onClick={() => handleDestinationRemove(idx)}
                            className="ml-0.5 text-blue-400 hover:text-blue-700 leading-none"
                            title={`删除 ${city.name}`}
                            disabled={(formData.destinationCities || []).length <= 1}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    )
                  ))}
                </div>
              )}
              {/* 搜索添加城市 */}
              {!disabled && (formData.destinationCities || []).length < 10 && (
                <AsyncSearchSelect
                  label=""
                  value=""
                  displayText=""
                  onSearch={handleCitySearch}
                  onChange={handleDestinationAdd}
                  loadDefaultOptions={() => apiClient.getHotCities()}
                  formatOption={formatCityOption}
                  placeholder="点击选择或搜索目的城市..."
                  required={false}
                  disabled={disabled}
                />
              )}
              {/* 校验提示 */}
              {(() => {
                const cities = formData.destinationCities || []
                if (cities.length === 0) {
                  return <p className="hint-text text-red-500 mt-0.5">请至少选择一个目的城市</p>
                }
                const validation = validateDestinationCities(cities, formData.departureCity)
                if (!validation.isValid) {
                  return <p className="hint-text text-red-500 mt-0.5">{validation.message}</p>
                }
                if (cities.length >= 10) {
                  return <p className="hint-text text-gray-400 mt-0.5">已达最多 10 个目的城市</p>
                }
                return null
              })()}
              {/* 郑州目的城市推广提示 */}
              {(formData.destinationCities || []).some(city => city.name === '郑州市') && (
                <p className="hint-text text-red-500 mt-0.5">去郑州，首选善水居，住自家的房！灵犀搜索预定</p>
              )}
            </div>
          </div>
          {/* 出发城市与目的城市相同时的错误提示（兼容存量数据） */}
          {formData.departureCity && formData.destinationCity &&
            !formData.destinationCities?.length &&
            formData.departureCity === formData.destinationCity && (
            <p className="hint-text text-red-500 -mt-2">
              出差城市不能与出发城市相同，请重新选择
            </p>
          )}

          {/* 出差事由 */}
          <div className="required-field">
            <label className="heading-2 mb-clay-sm block">出差事由 <span className="text-red-500">*</span></label>
            <textarea
              placeholder="请详细说明出差原因和主要工作内容"
              className="clay-textarea"
              rows={3}
              maxLength={200}
              value={formData.reason || ''}
              onChange={(e) => onChange({ reason: e.target.value })}
              disabled={disabled}
              readOnly={disabled}
            />
            <p className="hint-text mt-1">
              {formData.reason?.length || 0}/200 字符
            </p>
          </div>
        </div>
      )}

      {/* 多段行程模式 - 当 multiSegment = true 时显示 */}
      {formData.multiSegment && (
        <div className="space-y-clay-md">
          <h4 className="text-clay-lg font-semibold text-slate-900 mb-clay-md">
            行程段管理
          </h4>
          <TripSegmentList
            segments={formData.tripSegments || []}
            onChange={(tripSegments) =>
              onChange({ tripSegments })
            }
            disabled={disabled}  // 🔑 传递禁用状态
          />

        </div>
      )}

      {/* 预估费用 */}
      <div className={formData.expenseType !== 'SELF_PAID' ? 'required-field' : ''}>
        <label className="heading-2 mb-clay-sm block">
          预估费用（元）{formData.expenseType !== 'SELF_PAID' && <span className="text-red-500"> *</span>}
        </label>
        <input
          type="number"
          placeholder="0"
          min="0"
          max="9999999999"
          className={`clay-input ${formData.expenseType === 'SELF_PAID' ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`}
          value={expenseInputValue}
          onChange={(e) => {
            if (formData.expenseType === 'SELF_PAID') return
            const raw = e.target.value
            // 只允许非负数字（含小数），且不超过 DB DECIMAL(12,2) 上限
            if (raw !== '' && (isNaN(Number(raw)) || Number(raw) < 0)) return
            setExpenseInputValue(raw)
            const numValue = raw === '' ? 0 : Math.min(9999999999, Math.max(0, parseFloat(raw) || 0))
            onChange({ estimatedExpense: numValue })
          }}
          onBlur={() => {
            if (formData.expenseType === 'SELF_PAID') return
            handleEstimatedExpenseBlur()
          }}
          disabled={disabled || formData.expenseType === 'SELF_PAID'}
        />
        {formData.expenseType === 'SELF_PAID' ? (
          <p className="hint-text mt-1">自费出差无需填写预估费用，固定为 0 元</p>
        ) : (
          <p className="hint-text mt-1 leading-relaxed">
            预估金额仅填写出差申请单发起人自己，预计产生的机票/火车、住宿、预计打车费合计，同行人出差预计金额填写在同行人信息行。如有疑问可咨询财务AI助手~
          </p>
        )}
      </div>
    </div>
  )
}

export default CrossCityTripForm
